import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Newsletter } from "@/components/Newsletter";
import {
  ChevronRight, Building2, Briefcase, Palette, UserSearch, Users, Globe,
  CalendarDays, TrendingUp
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const timeAgo = (iso) => {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins} dk önce`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} saat önce`;
  const days = Math.floor(hours / 24);
  return days === 1 ? "Dün" : `${days} gün önce`;
};

/* ============================
   SECTION LAYOUTS (WP Style)
   ============================ */

/* Layout A: "Latest" - 4 col grid, title left + thumbnail right */
const LayoutLatest = ({ articles, title }) => (
  <section className="border-t-2 border-stone-900 pt-6 pb-10">
    <h2 className="text-lg md:text-xl font-bold mb-6 flex items-center gap-1" style={{ fontFamily: "'Playfair Display', serif" }}>
      {title} <ChevronRight className="w-4 h-4" />
    </h2>
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-6">
      {articles.map((a) => (
        <Link key={a.id} to={`/haber/${a.slug || a.id}`} className="group flex gap-3" data-testid={`latest-${a.id}`}>
          <div className="flex-1 min-w-0">
            <h3 className="text-sm md:text-base font-semibold text-stone-900 leading-snug group-hover:text-[#1B4F72] transition-colors line-clamp-3" style={{ fontFamily: "'Playfair Display', serif" }}>
              {a.title}
            </h3>
            <span className="font-mono text-[9px] text-stone-400 uppercase tracking-widest mt-1.5 block">
              {timeAgo(a.published_at)}
            </span>
          </div>
          <img src={a.image_url} alt="" className="w-24 h-20 object-cover flex-shrink-0" />
        </Link>
      ))}
    </div>
  </section>
);

/* Layout B: "Better Living" - 4 col, large image top + title below */
const LayoutCards = ({ articles, title }) => (
  <section className="border-t-2 border-stone-900 pt-6 pb-10">
    <h2 className="text-lg md:text-xl font-bold mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
      {title}
    </h2>
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
      {articles.map((a) => (
        <Link key={a.id} to={`/haber/${a.slug || a.id}`} className="group" data-testid={`card-${a.id}`}>
          <div className="aspect-[4/3] overflow-hidden mb-3">
            <img src={a.image_url} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
          </div>
          <span className="font-mono text-[8px] text-stone-400 uppercase tracking-widest">{a.source}</span>
          <h3 className="text-sm md:text-base font-semibold text-stone-900 leading-snug group-hover:text-[#1B4F72] transition-colors mt-1 line-clamp-3" style={{ fontFamily: "'Playfair Display', serif" }}>
            {a.title}
          </h3>
        </Link>
      ))}
    </div>
  </section>
);

/* Layout C: "Politics" - Featured article (text+image) + 2-col headline grid */
const LayoutFeatured = ({ articles, title }) => {
  const main = articles[0];
  const rest = articles.slice(1, 7);
  if (!main) return null;
  return (
    <section className="border-t-2 border-stone-900 pt-6 pb-10">
      <h2 className="text-lg md:text-xl font-bold mb-6 flex items-center gap-1" style={{ fontFamily: "'Playfair Display', serif" }}>
        {title} <ChevronRight className="w-4 h-4" />
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
        {/* Featured */}
        <Link to={`/haber/${main.slug || main.id}`} className="md:col-span-5 group" data-testid={`featured-${main.id}`}>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-1 gap-4">
            <div>
              <h3 className="text-xl md:text-2xl font-bold text-stone-900 leading-tight group-hover:text-[#1B4F72] transition-colors mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>
                {main.title}
              </h3>
              <p className="text-sm text-stone-500 line-clamp-4 mb-2">{main.summary}</p>
              <span className="font-mono text-[9px] text-stone-400 uppercase tracking-widest">{main.author}</span>
            </div>
            <div className="aspect-[4/3] overflow-hidden">
              <img src={main.image_url} alt="" className="w-full h-full object-cover" />
            </div>
          </div>
        </Link>
        {/* Headline grid */}
        <div className="md:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-0">
          {rest.map((a) => (
            <Link key={a.id} to={`/haber/${a.slug || a.id}`} className="group py-3 border-b border-stone-200" data-testid={`headline-${a.id}`}>
              <h4 className="text-sm font-semibold text-stone-900 leading-snug group-hover:text-[#1B4F72] transition-colors line-clamp-2" style={{ fontFamily: "'Playfair Display', serif" }}>
                {a.title}
              </h4>
              <p className="text-xs text-stone-400 leading-relaxed mt-1 line-clamp-5">{a.summary}</p>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

/* Layout D: Two side-by-side sections (Business + National style) */
const LayoutDualHalf = ({ articles, title, borderRight = false }) => {
  const main = articles[0];
  const rest = articles.slice(1, 5);
  if (!main) return null;

  // If only 1 article: full-width featured card
  if (rest.length === 0) {
    return (
      <div className={`border-t-2 border-stone-900 pt-6 pb-10 ${borderRight ? "md:pr-8 md:border-r border-stone-200" : "md:pl-8"}`}>
        <h2 className="text-lg md:text-xl font-bold mb-6 flex items-center gap-1" style={{ fontFamily: "'Playfair Display', serif" }}>
          {title} <ChevronRight className="w-4 h-4" />
        </h2>
        <Link to={`/haber/${main.slug || main.id}`} className="group block">
          <div className="aspect-[16/9] overflow-hidden mb-3">
            <img src={main.image_url} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
          </div>
          <h3 className="text-lg font-semibold text-stone-900 leading-snug group-hover:text-[#1B4F72] transition-colors mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
            {main.title}
          </h3>
          <p className="text-sm text-stone-500 line-clamp-3">{main.summary}</p>
        </Link>
      </div>
    );
  }

  return (
    <div className={`border-t-2 border-stone-900 pt-6 pb-10 ${borderRight ? "md:pr-8 md:border-r border-stone-200" : "md:pl-8"}`}>
      <h2 className="text-lg md:text-xl font-bold mb-6 flex items-center gap-1" style={{ fontFamily: "'Playfair Display', serif" }}>
        {title} <ChevronRight className="w-4 h-4" />
      </h2>
      <div className="grid grid-cols-2 gap-4">
        <Link to={`/haber/${main.slug || main.id}`} className="group">
          <div className="aspect-[3/4] overflow-hidden mb-2">
            <img src={main.image_url} alt="" className="w-full h-full object-cover" />
          </div>
          <h3 className="text-base font-semibold text-stone-900 leading-snug group-hover:text-[#1B4F72] transition-colors" style={{ fontFamily: "'Playfair Display', serif" }}>
            {main.title}
          </h3>
        </Link>
        <div className="space-y-0">
          {rest.map((a) => (
            <Link key={a.id} to={`/haber/${a.slug || a.id}`} className="group block py-2.5 border-b border-stone-200 last:border-0">
              <h4 className="text-sm text-stone-900 leading-snug group-hover:text-[#1B4F72] transition-colors line-clamp-2" style={{ fontFamily: "'Playfair Display', serif" }}>
                {a.title}
              </h4>
              <p className="text-xs text-stone-400 leading-relaxed mt-1 line-clamp-5">{a.summary}</p>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
};

const LayoutDual = ({ left, right, leftTitle, rightTitle }) => (
  <section className="grid grid-cols-1 md:grid-cols-2 gap-0">
    <LayoutDualHalf articles={left} title={leftTitle} borderRight />
    <LayoutDualHalf articles={right} title={rightTitle} />
  </section>
);


/* ============================
   MAIN HOME PAGE
   ============================ */
export default function HomePage() {
  const [articles, setArticles] = useState([]);
  const [stats, setStats] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [artRes, statRes, evRes] = await Promise.all([
          axios.get(`${API}/articles?limit=100`),
          axios.get(`${API}/statistics`),
          axios.get(`${API}/events?upcoming=true&limit=20`),
        ]);
        setArticles(artRes.data);
        setStats(statRes.data);
        setEvents(evRes.data.slice(0, 12));
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    fetchAll();
  }, []);

  if (loading) {
    return (
      <div className="max-w-screen-xl mx-auto px-4 md:px-8 py-16 space-y-8">
        {[1, 2, 3].map((i) => <div key={i} className="skeleton h-64 w-full" />)}
      </div>
    );
  }

  // Group articles by source
  const bySource = {};
  articles.forEach((a) => {
    const src = a.source || "Diğer";
    if (!bySource[src]) bySource[src] = [];
    bySource[src].push(a);
  });

  // Ensure hero shows diverse sources (max 1 per source)
  const heroPool = [];
  const usedSources = new Set();
  for (const a of articles) {
    const src = a.source || "";
    if (!usedSources.has(src)) {
      heroPool.push(a);
      usedSources.add(src);
    }
    if (heroPool.length >= 6) break;
  }
  if (heroPool.length < 6) {
    for (const a of articles) {
      if (!heroPool.find(h => h.id === a.id)) {
        heroPool.push(a);
        if (heroPool.length >= 6) break;
      }
    }
  }

  const heroArticle = heroPool[0];
  const secondaryHero = heroPool.slice(1, 6);
  const heroIds = new Set(heroPool.map(a => a.id));

  // Source groups for sections (exclude hero articles, sorted by count)
  const sourceGroups = Object.entries(bySource)
    .map(([name, arts]) => ({ name, articles: arts.filter(a => !heroIds.has(a.id)) }))
    .filter(g => g.articles.length > 0)
    .sort((a, b) => b.articles.length - a.articles.length);

  return (
    <div data-testid="home-page" className="bg-[#FAFAF9]">
      {/* ===== MANŞET ===== */}
      <section className="max-w-screen-xl mx-auto px-4 md:px-8 pt-6 pb-8 border-b-2 border-stone-900">
        <h2 className="font-mono text-xs uppercase tracking-widest text-[#1B4F72] font-bold mb-5">Manşet</h2>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          {heroArticle && (
            <Link to={`/haber/${heroArticle.slug || heroArticle.id}`} className="md:col-span-7 group" data-testid="hero-main">
              <div className="aspect-[16/10] overflow-hidden mb-4">
                <img src={heroArticle.image_url} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              </div>
              <Badge variant="outline" className="font-mono text-[9px] uppercase tracking-widest border-[#1B4F72] text-[#1B4F72] mb-2">
                {heroArticle.source}
              </Badge>
              <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight leading-[1.1] text-stone-950 group-hover:text-[#1B4F72] transition-colors mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>
                {heroArticle.title}
              </h1>
              <p className="text-sm md:text-base text-stone-600 line-clamp-3">{heroArticle.summary}</p>
              <span className="font-mono text-[9px] text-stone-400 uppercase tracking-widest mt-2 block">
                {heroArticle.author} &middot; {heroArticle.source} &middot; {timeAgo(heroArticle.published_at)}
              </span>
            </Link>
          )}
          <div className="md:col-span-5 flex flex-col">
            {secondaryHero.map((a, i) => (
              <Link key={a.id} to={`/haber/${a.slug || a.id}`} className={`group flex gap-4 py-4 ${i < secondaryHero.length - 1 ? "border-b border-stone-200" : ""}`}>
                <div className="flex-1 min-w-0">
                  <Badge variant="outline" className="font-mono text-[8px] uppercase tracking-widest border-stone-300 mb-1.5">
                    {a.source}
                  </Badge>
                  <h2 className="text-base md:text-lg font-semibold text-stone-900 leading-snug group-hover:text-[#1B4F72] transition-colors line-clamp-3" style={{ fontFamily: "'Playfair Display', serif" }}>
                    {a.title}
                  </h2>
                  <span className="font-mono text-[9px] text-stone-400 uppercase tracking-widest mt-1 block">
                    {a.source} &middot; {timeAgo(a.published_at)}
                  </span>
                </div>
                <img src={a.image_url} alt="" className="w-28 h-20 object-cover flex-shrink-0" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ===== SOURCE-BASED SECTIONS ===== */}
      <div className="max-w-screen-xl mx-auto px-4 md:px-8">

        {/* Dynamic source groups — layout based on article count */}
        {sourceGroups.map((group, idx) => {
          const count = group.articles.length;
          if (count >= 5) {
            // Large source: alternating layouts
            const layout = idx % 2;
            if (layout === 0) return <LayoutLatest key={group.name} articles={group.articles.slice(0, 8)} title={group.name} />;
            return <LayoutFeatured key={group.name} articles={group.articles.slice(0, 7)} title={group.name} />;
          }
          if (count >= 3) {
            // Medium source: cards layout (always fills nicely)
            return <LayoutCards key={group.name} articles={group.articles.slice(0, 4)} title={group.name} />;
          }
          // Small sources will be paired below
          return null;
        })}

        {/* Merge small sources into one filled grid with source labels on each */}
        {(() => {
          const small = sourceGroups.filter(g => g.articles.length < 3);
          if (small.length === 0) return null;
          const mixedArticles = [];
          small.forEach(g => g.articles.forEach(a => mixedArticles.push(a)));
          // Fill to at least 4 with overflow from large sources
          if (mixedArticles.length < 4) {
            const large = sourceGroups.filter(g => g.articles.length >= 5);
            for (const g of large) {
              for (const a of g.articles.slice(4)) {
                if (mixedArticles.length >= 4) break;
                if (!mixedArticles.find(m => m.id === a.id)) mixedArticles.push(a);
              }
              if (mixedArticles.length >= 4) break;
            }
          }
          if (mixedArticles.length === 0) return null;

          const title = small.map(g => g.name).join(" · ") + (mixedArticles.length > small.reduce((s,g) => s+g.articles.length, 0) ? " · Diğer" : "");

          return (
            <section className="border-t-2 border-stone-900 pt-6 pb-10">
              <h2 className="text-lg md:text-xl font-bold mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
                {title}
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {mixedArticles.slice(0, 8).map((a) => (
                  <Link key={a.id} to={`/haber/${a.slug || a.id}`} className="group" data-testid={`mixed-${a.id}`}>
                    <div className="aspect-[4/3] overflow-hidden mb-3">
                      <img src={a.image_url} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    </div>
                    <span className="font-mono text-[8px] text-[#1B4F72] uppercase tracking-widest font-bold">{a.source}</span>
                    <h3 className="text-sm md:text-base font-semibold text-stone-900 leading-snug group-hover:text-[#1B4F72] transition-colors mt-1 line-clamp-3" style={{ fontFamily: "'Playfair Display', serif" }}>
                      {a.title}
                    </h3>
                  </Link>
                ))}
              </div>
            </section>
          );
        })()}

        {/* ===== Fuar Takvimi + İstatistik Bar ===== */}
        <section className="border-t-2 border-stone-900 pt-6 pb-10">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
            {/* Yaklaşan Fuarlar */}
            <div className="md:col-span-7">
              <h2 className="text-lg md:text-xl font-bold mb-6 flex items-center gap-1" style={{ fontFamily: "'Playfair Display', serif" }}>
                <CalendarDays className="w-5 h-5 text-[#1B4F72]" /> Yaklaşan Fuarlar <ChevronRight className="w-4 h-4" />
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-0">
                {events.map((e) => (
                  <div key={e.id} className="py-3 border-b border-stone-200">
                    <h4 className="text-sm font-semibold text-stone-900 leading-snug">{e.name}</h4>
                    <p className="font-mono text-[9px] text-stone-400 mt-1">
                      {e.country && <span className="text-[#003049]">{e.country}</span>}
                      {e.country && " · "}{e.city} · {e.start_date ? new Date(e.start_date).toLocaleDateString("tr-TR", { day: "numeric", month: "short", year: "numeric" }) : ""}
                    </p>
                  </div>
                ))}
              </div>
              <Link to="/takvim" className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-widest text-stone-500 hover:text-[#1B4F72] mt-4">
                Tüm Takvim ({stats?.total_events || 0}+ etkinlik) <ChevronRight className="w-3 h-3" />
              </Link>
            </div>
            {/* Stats + Newsletter */}
            <div className="md:col-span-5 space-y-6">
              {stats && (
                <div className="bg-stone-900 text-white p-6">
                  <h3 className="font-mono text-[10px] uppercase tracking-widest text-stone-400 mb-4">Rakamlarla Expo Haber</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-3xl font-bold" style={{ fontFamily: "'Playfair Display', serif" }}>{stats.total_articles}</p>
                      <p className="font-mono text-[9px] text-stone-400 uppercase">Makale</p>
                    </div>
                    <div>
                      <p className="text-3xl font-bold" style={{ fontFamily: "'Playfair Display', serif" }}>{stats.total_events?.toLocaleString("tr-TR")}</p>
                      <p className="font-mono text-[9px] text-stone-400 uppercase">Etkinlik</p>
                    </div>
                    <div>
                      <p className="text-3xl font-bold" style={{ fontFamily: "'Playfair Display', serif" }}>{stats.total_views?.toLocaleString("tr-TR")}</p>
                      <p className="font-mono text-[9px] text-stone-400 uppercase">Görüntülenme</p>
                    </div>
                    <div>
                      <p className="text-3xl font-bold" style={{ fontFamily: "'Playfair Display', serif" }}>34+</p>
                      <p className="font-mono text-[9px] text-stone-400 uppercase">Ülke</p>
                    </div>
                  </div>
                </div>
              )}
              <div className="bg-white border border-stone-200 p-5">
                <Newsletter compact />
              </div>
            </div>
          </div>
        </section>

        {/* ===== FUTURE PLACEHOLDERS ===== */}
        <section className="border-t-2 border-stone-900 pt-6 pb-10">
          <h2 className="text-lg md:text-xl font-bold mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
            Sektör Platformu
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Firma Haberleri */}
            <div className="border border-stone-200 p-5 bg-white" data-testid="company-news-placeholder">
              <div className="flex items-center gap-2 mb-3">
                <Briefcase className="w-4 h-4 text-[#1B4F72]" />
                <h3 className="font-mono text-[10px] uppercase tracking-widest font-bold">Firma Haberleri</h3>
                <Badge className="ml-auto bg-[#D4AF37] text-stone-900 font-mono text-[7px]">Yakında</Badge>
              </div>
              <p className="text-xs text-stone-500 mb-2">Stand firmaları kendi haberlerini ve projelerini paylaşabilecek.</p>
              <Button disabled className="rounded-none font-mono text-[9px] uppercase tracking-widest w-full mt-2" variant="outline" size="sm">Haber Gönder</Button>
            </div>
            {/* Organizatör Haberleri */}
            <div className="border border-stone-200 p-5 bg-white" data-testid="organizer-news-placeholder">
              <div className="flex items-center gap-2 mb-3">
                <Globe className="w-4 h-4 text-[#003049]" />
                <h3 className="font-mono text-[10px] uppercase tracking-widest font-bold">Organizatör Haberleri</h3>
                <Badge className="ml-auto bg-[#D4AF37] text-stone-900 font-mono text-[7px]">Yakında</Badge>
              </div>
              <p className="text-xs text-stone-500 mb-2">Fuar organizatörleri etkinliklerini tanıtabilecek.</p>
              <Button disabled className="rounded-none font-mono text-[9px] uppercase tracking-widest w-full mt-2" variant="outline" size="sm">Duyuru Yayınla</Button>
            </div>
            {/* Firma Rehberi */}
            <div className="border border-stone-200 p-5 bg-white" data-testid="directory-placeholder">
              <div className="flex items-center gap-2 mb-3">
                <Building2 className="w-4 h-4 text-stone-700" />
                <h3 className="font-mono text-[10px] uppercase tracking-widest font-bold">Firma Rehberi</h3>
                <Badge className="ml-auto bg-[#D4AF37] text-stone-900 font-mono text-[7px]">Yakında</Badge>
              </div>
              <p className="text-xs text-stone-500 mb-2">Lojistik, catering, host/hostess firmalarını keşfedin.</p>
              <Button disabled className="rounded-none font-mono text-[9px] uppercase tracking-widest w-full mt-2" variant="outline" size="sm">Firmaları Gör</Button>
            </div>
            {/* Portfolyolar */}
            <div className="border border-stone-200 p-5 bg-white" data-testid="portfolio-placeholder">
              <div className="flex items-center gap-2 mb-3">
                <Palette className="w-4 h-4 text-[#1B4F72]" />
                <h3 className="font-mono text-[10px] uppercase tracking-widest font-bold">Portfolyolar</h3>
                <Badge className="ml-auto bg-[#D4AF37] text-stone-900 font-mono text-[7px]">Yakında</Badge>
              </div>
              <p className="text-xs text-stone-500 mb-2">Stand tasarımcıları ve 3D görselciler portfolyolarını sergileyebilecek.</p>
              <div className="grid grid-cols-3 gap-1 mt-2">
                {[1, 2, 3].map((i) => <div key={i} className="aspect-square bg-stone-100" />)}
              </div>
            </div>
            {/* İş İlanları */}
            <div className="border border-stone-200 p-5 bg-white" data-testid="jobs-placeholder">
              <div className="flex items-center gap-2 mb-3">
                <UserSearch className="w-4 h-4 text-[#003049]" />
                <h3 className="font-mono text-[10px] uppercase tracking-widest font-bold">İş İlanları</h3>
                <Badge className="ml-auto bg-[#D4AF37] text-stone-900 font-mono text-[7px]">Yakında</Badge>
              </div>
              <p className="text-xs text-stone-500 mb-2">Freelancerlar iş arayabilir, firmalar ilan verebilir.</p>
              <Button disabled className="rounded-none font-mono text-[9px] uppercase tracking-widest w-full mt-2" variant="outline" size="sm">İlan Ver</Button>
            </div>
            {/* Firma Profilleri */}
            <div className="border border-stone-200 p-5 bg-white" data-testid="company-profiles-placeholder">
              <div className="flex items-center gap-2 mb-3">
                <Users className="w-4 h-4 text-stone-700" />
                <h3 className="font-mono text-[10px] uppercase tracking-widest font-bold">Firma Profilleri</h3>
                <Badge className="ml-auto bg-[#D4AF37] text-stone-900 font-mono text-[7px]">Yakında</Badge>
              </div>
              <p className="text-xs text-stone-500 mb-2">Fuar firmaları profil ve portfolyo oluşturabilecek.</p>
              <Button disabled className="rounded-none font-mono text-[9px] uppercase tracking-widest w-full mt-2" variant="outline" size="sm">Profil Oluştur</Button>
            </div>
          </div>
        </section>

      </div>
    </div>
  );
}
